import FigureCaption from "@/components/TipTapEditor/extensions/FigureCaption";
import FontSize from "@/components/TipTapEditor/extensions/FontSize";
import FormulaBlockNode from "@/components/TipTapEditor/extensions/FormulaBlockNode";
import FormulaInlineNode from "@/components/TipTapEditor/extensions/FormulaInlineNode";
import ResizableImage from "@/components/TipTapEditor/extensions/ResizeableImage";
import iframe from "@/components/TipTapEditor/extensions/iframe";
import { FileInput } from "@/services/fobizzToolsApi";
import { flash } from "@/utils/flash";
import { posToDOMRect } from "@tiptap/core";
import type { Editor, Extension, EditorOptions } from "@tiptap/core";
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
import FontFamily from "@tiptap/extension-font-family";
import Highlight from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Table from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import TextAlign from "@tiptap/extension-text-align";
import TextStyle from "@tiptap/extension-text-style";
import TextUnderline from "@tiptap/extension-underline";
import StarterKit from "@tiptap/starter-kit";
import DOMPurify from "dompurify";
import "highlight.js/styles/github.css";
import { common, createLowlight } from "lowlight";

const lowlight = createLowlight(common);

type Range = { from: number; to: number };

type DOMRect = {
  top: number;
  left: number;
  width: number;
  height: number;
  bottom: number;
  right: number;
  x: number;
  y: number;
};

export type ReturnTypes = {
  focus: () => void;
  insertContent: (content: string, options?: Record<string, unknown>) => void;
  insertContentAt: (
    position: number | Range,
    value: string,
    options?: Record<string, unknown>
  ) => void;
  insertContentAtCursor: (
    content: string,
    options?: Record<string, unknown>
  ) => void;
  getSelectedText: () => string | undefined;
  getSelection: () => Range | undefined;
  deleteSelection: () => void;
  setTextSelection: (position: number | Range) => void;
  getCursorPosition: () => number | undefined;
  getCursorPositionToDOMRect: () => DOMRect | undefined;
  handleDrop: (event: DragEvent) => void;
  handlePaste: (event: ClipboardEvent) => void;
};

export const setupEditorProps = (): EditorOptions["editorProps"] => {
  return {
    transformPastedHTML(html: string) {
      const sanitized = DOMPurify.sanitize(html, {
        FORBID_ATTR: ["style"],
      });
      return sanitized;
    },
    attributes: {
      class: "ProseMirror--naked prosa",
    },
  };
};

export const setupExtensions = ({
  enableHistory,
  placeholder,
}: {
  enableHistory: boolean;
  placeholder: string;
}): Extension[] => {
  return [
    StarterKit.configure({
      // The Collaboration extension comes with its own history handling
      history: enableHistory || true,
      codeBlock: false,
    }),
    Highlight,
    TextAlign.configure({
      types: ["heading", "paragraph"],
    }),
    TextStyle,
    TextUnderline,
    FontFamily,
    Link.configure({ openOnClick: true }),
    Placeholder.configure({ placeholder: placeholder }),
    Table.configure({
      HTMLAttributes: {
        class: "table table-bordered",
      },
      allowTableNodeSelection: false,
    }),
    TableRow,
    TableHeader,
    TableCell,
    CodeBlockLowlight.configure({
      lowlight,
    }),
    FontSize,
    FigureCaption,
    iframe,
    ResizableImage.configure({
      HTMLAttributes: {
        class: "resizable-image",
      },
    }),
    FormulaBlockNode,
    FormulaInlineNode,
  ];
};

export const setupMinimalExtensions = (): Extension[] => {
  return [StarterKit, Link.configure({ openOnClick: false }), Highlight];
};

type TipTapEditorActionsOptions = {
  parentType?: string;
  parentId?: string;
};

export function useTipTapEditorActions(
  editor: Ref<Editor>,
  options: TipTapEditorActionsOptions
): ReturnTypes {
  const insertContent = (
    content: string,
    options?: Record<string, unknown>
  ) => {
    if (editor.value) {
      editor.value.commands.insertContent(content, options);
    }
  };

  const insertContentAt = (
    position: number | Range,
    content: string,
    options?: Record<string, unknown>
  ) => {
    if (editor.value) {
      editor.value.commands.insertContentAt(position, content, options);
    }
  };

  const insertContentAtCursor = (
    content: string,
    options?: Record<string, unknown>
  ) => {
    if (editor.value) {
      const cursorPosition = editor.value.state.selection.$anchor.pos;
      if (cursorPosition) {
        // insert
        editor.value.commands.insertContentAt(cursorPosition, content, options);
      } else {
        // append
        editor.value.commands.insertContent(content, options);
      }
    }
  };

  const getCursorPosition = (): number | undefined => {
    const state = editor.value.state;
    const selection = state.selection;
    if (selection) {
      return selection.$anchor.pos;
    }
  };

  const getCursorPositionToDOMRect = (): DOMRect | undefined => {
    const state = editor.value.view.state;
    const selection = state.selection;
    if (selection) {
      const { from, to } = selection;
      return posToDOMRect(editor.value.view, from, to);
    }
  };

  const getSelectedText = (): string | undefined => {
    const state = editor.value.view.state;
    const selection = state.selection;

    if (selection) {
      const { from, to } = selection;
      const text = state.doc.textBetween(from, to, " ");
      return text;
    }
  };

  const getSelection = (): Range | undefined => {
    const state = editor.value.view.state;
    const selection = state.selection;
    if (selection) {
      const { from, to } = selection;
      return { from, to } as Range;
    }

    return undefined;
  };

  const deleteSelection = () => {
    editor.value.commands.deleteSelection();
  };

  const setTextSelection = (position: number | Range) => {
    editor.value.commands.setTextSelection(position);
  };

  const focus = () => {
    if (editor.value) {
      editor.value.commands.focus();
    }
  };

  const { uploadFile } = useDirectUploadShrine();
  const progress = ref();

  const { imageAttachments: imageAttachtmentService } = useApiClient();

  const uploadImage = (file: File) => {
    uploadFile(file, {
      errorHandler: (_error: unknown) => {
        progress.value = 0;
      },
      successHandler: async (file: FileInput) => {
        const response = await imageAttachtmentService.createImageAttachment({
          requestBody: {
            imageAttachment: {
              attachableType: options.parentType,
              attachableId: options.parentId,
              file,
            },
          },
        });

        const url = `/files/${response.id}`;
        editor.value
          .chain()
          .focus()
          .setImage({ src: url, blobSid: response.id })
          .run();
        progress.value = 0;
      },
      progressHandler: (_progress: number) => {
        progress.value = _progress;
      },
    });
  };

  const MAX_FILE_SIZE = 5;
  const SUPPORTED_FILE_TYPES = ["image/jpeg", "image/png"];

  const reader = new FileReader();

  const addImage = (file: File) => {
    const filesize = file.size / 1024 / 1024;

    if (!SUPPORTED_FILE_TYPES.includes(file.type)) {
      flash(FlashKindEnum.ALERT, "Only images are allowed");
      return;
    }

    if (filesize > MAX_FILE_SIZE) {
      flash(FlashKindEnum.ALERT, `Only images smaller than ${MAX_FILE_SIZE}`);
      return;
    }

    reader.onload = () => {
      const img = new Image();
      img.src = reader.result as string;
      img.onload = () => {
        uploadImage(file);
      };
    };

    reader.readAsDataURL(file);
  };

  const handleDrop = (event: DragEvent) => {
    event.preventDefault();

    const files = Array.from(event.dataTransfer?.files || []);
    if (files && files.length > 0) {
      const file = files[0];
      addImage(file);
    }
  };

  const handlePaste = (event: ClipboardEvent) => {
    if (event.clipboardData?.files && event.clipboardData?.files.length > 0) {
      event.preventDefault();
      const files = Array.from(event.clipboardData?.files || []);
      if (files && files.length > 0) {
        const file = files[0];
        addImage(file);
      }
    }
  };

  return {
    focus,
    insertContent,
    insertContentAt,
    insertContentAtCursor,
    setTextSelection,
    getSelection,
    getSelectedText,
    getCursorPosition,
    getCursorPositionToDOMRect,
    deleteSelection,
    handleDrop,
    handlePaste,
  };
}
